import {
  filter,
  fromEvent,
  mergeWith,
  Observable,
  Subject,
  switchMapTo,
  withLatestFrom,
} from 'rxjs';
import { Targeting } from '@tv2-player/static-commercial/hooks';
import { getTargeting } from '@tv2-player/static-commercial';
import { tapOnce } from '../utils/tapOnce';
import store, { IS_SHOWING_BREAKS, PPID } from '../store';
import { render } from './render';
import { AdUnit, GoogleTag, GoogleTagObject } from './types';
import { chromecastAdUnit } from '../config/googletag';
import { logPauseAds } from './logPauseAds';
import cast from '../types/chromecast-caf-receiver';

declare global {
  interface Window {
    googletag: any;
  }
}

const staticCommercialContainer = document.getElementById('staticCommercialContainer');

/**
 * Hides the ad overlay by removing one of the visibility-classes
 * @param {('shouldShow'|'canShow')} [className="shouldShow"]
 *  className to remove, defaults to `shouldShow`
 */
export const hideAdOverlay = (className: 'shouldShow' | 'canShow' = 'shouldShow') => {
  staticCommercialContainer
    ?.querySelectorAll('div')
    .forEach((element: HTMLDivElement) => element.classList.remove(className));
};

/**
 * Shows the ad overlay by adding one of the visibility-classes
 * @param {('shouldShow'|'canShow')} [className="shouldShow"]
 *  className to add, defaults to `shouldShow`
 */
export const showAdOverlay = (className: 'shouldShow' | 'canShow' = 'shouldShow') => {
  staticCommercialContainer
    ?.querySelectorAll('div')
    .forEach((element: HTMLDivElement) => element.classList.add(className));
};

const slotRenderEndedHandler = (event: GoogleTag.events.SlotRenderEndedEvent) => {
  if (!event.isEmpty) {
    // Add canShow class when slot is filled
    showAdOverlay('canShow');
  }
  currentAd?.removeEventListener('slotRenderEnded', slotRenderEndedHandler);
  clearInterval(checkCanShowInterval);
};
let currentAd: GoogleTag.PubAdsService;
let currentSlot: GoogleTag.Slot | undefined;
let checkCanShowInterval: NodeJS.Timeout;
let adsPrepared = false;

/**
 * Prepares Google ads
 * @param {AdUnit} adUnit Object containing expected ad params
 * @param playerManager
 */
const preparePubads = (adUnit: AdUnit, playerManager: any, targeting: Targeting) => {
  if (!adsPrepared) {
    adsPrepared = true;
    const { adUnitPath, size, divId } = adUnit;
    const googletag: GoogleTagObject = window.googletag || { cmd: [] };

    googletag.cmd.push(() => {
      currentSlot = googletag.defineSlot(adUnitPath, size, divId)?.addService(googletag.pubads());
      targeting?.forEach(([key, value]) => {
        googletag.pubads()?.setTargeting(key, value);
      });
      currentAd = googletag.pubads();
      currentAd?.disableInitialLoad();

      const ppid = store.get(PPID);
      if (ppid) {
        currentAd.setPublisherProvidedId(ppid);
      }
      googletag.enableServices();

      /*
    We can't be sure the ad is ready to be shown, so we need to keep doing this until
    slotRenderEnded has been called
     */
      checkCanShowInterval = setInterval(() => {
        googletag.cmd.push(() => googletag.display(divId));
      }, 1000);

      currentAd.addEventListener('slotRenderEnded', slotRenderEndedHandler);

      // Remove Google Ads when MEDIA_FINISHED
      playerManager.addEventListener(
        cast.framework.events.EventType.MEDIA_FINISHED,
        tearDownPubads
      );
    });
  }
};

const refreshPubads = () => {
  const googletag: GoogleTagObject = window.googletag || { cmd: [] };

  // .refresh adds the following scripts to <head/>
  const previousLinks = document.querySelectorAll('link[href*="adsid/integrator.js"]');
  const previousScripts = document.querySelectorAll('script[src*="adsid/integrator.js"]');
  // we remove them so we don't flood <head/>
  previousLinks.forEach(prevLink => prevLink.remove());
  previousScripts.forEach(prevScript => prevScript.remove());

  if (googletag?.pubadsReady && currentSlot) {
    currentAd?.refresh([currentSlot]);
  }
};

export const tearDownPubads = () => {
  adsPrepared = false;
  const { googletag }: { googletag?: GoogleTagObject } = window;

  if (googletag) {
    googletag.cmd.push(() => {
      googletag.destroySlots();
    });

    currentAd?.removeEventListener('slotRenderEnded', slotRenderEndedHandler);
    hideAdOverlay('canShow');
  }
};

export const staticCommercialProducer = (subject$: Subject<any>, playerManager: any) => {
  const { EventType } = cast.framework.events;

  const playerPlay$ = fromEvent<Event>(playerManager, EventType.REQUEST_PLAY);
  const playerPause$ = fromEvent<Event>(playerManager, EventType.REQUEST_PAUSE);
  const playerEnded$ = fromEvent<Event>(playerManager, EventType.ENDED);
  const breakStarted$ = fromEvent<Event>(playerManager, EventType.BREAK_STARTED);
  const breakEnded$ = fromEvent<Event>(playerManager, EventType.BREAK_ENDED);

  const event$ = subject$.pipe(
    filter(media => {
      return Boolean(media?.ads) && (!media?.metadata || !media.metadata.isLive);
    }),
    tapOnce<any>(media => {
      store.set(PPID, media.ads.ppid);
      const prerollAdUrl = media?.ads?.url;
      if (staticCommercialContainer) {
        staticCommercialContainer.innerHTML = render(chromecastAdUnit.divId);
        preparePubads(chromecastAdUnit, playerManager, getTargeting(prerollAdUrl));
      }
    }),
    switchMapTo<Observable<Event>>(
      new Observable<Event>().pipe(
        mergeWith(playerPlay$, playerPause$, playerEnded$, breakStarted$, breakEnded$)
      )
    )
  );
  logPauseAds(event$, breakStarted$, breakEnded$);

  event$.pipe(withLatestFrom(subject$)).subscribe(([{ type }, media]) => {
    switch (type) {
      case EventType.REQUEST_PLAY: {
        const prerollAdUrl = media?.ads?.url;
        preparePubads(chromecastAdUnit, playerManager, getTargeting(prerollAdUrl));
        hideAdOverlay('shouldShow');
        break;
      }
      case EventType.REQUEST_PAUSE:
        if (!store.get(IS_SHOWING_BREAKS)) {
          refreshPubads();
          showAdOverlay('shouldShow');
        }
        break;

      case EventType.ENDED:
        if (store.get(IS_SHOWING_BREAKS)) {
          tearDownPubads();
          hideAdOverlay('shouldShow');
        }
        break;

      case EventType.BREAK_ENDED:
        store.set(IS_SHOWING_BREAKS, false);
        break;

      case EventType.BREAK_STARTED:
        store.set(IS_SHOWING_BREAKS, true);
        break;

      default:
        break;
    }
  });
};
